import { getRewardfulReferral } from "../../utils/rewardful";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../config";
import CreditSlider from "./CreditSlider";
import "./UpgradePage.css";
import HardPaywall from "../../components/HardPaywall/HardPaywall";

const ACCENT = "#FF6B00";

const STARTER_MONTHLY = 15; // Starter is monthly only, as on OpusClip
const PRO_MONTHLY = 29; // per pack — checkout bills $29 × packs (stripeService.proPackItem)
const PRO_ANNUAL = 14.5;
const PREMIUM_MONTHLY = 145;
const PREMIUM_ANNUAL = 72.5;

// Per-credit pricing anchored to subscription tiers. Match config/planLimits.js
// (backend source of truth for actual Stripe charges).
const CREDIT_PACKS = [
  { id: "pack_50",   credits: 50,   price: 5,  note: null,         desc: "+50 min of video processing" },
  { id: "pack_150",  credits: 150,  price: 14, note: "Save 7%",    desc: "+150 min · Starter-equivalent" },
  { id: "pack_300",  credits: 300,  price: 27, note: "Save 10%",   desc: "+300 min · Pro-equivalent" },
  { id: "pack_1500", credits: 1500, price: 90, note: "Best Value", desc: "+25 hours · Premium-equivalent" },
];

// When false, the new pricing UI shows but Stripe still bills old rates,
// so subscribe clicks route to email instead of charging.
const NEW_PRICING_LIVE = process.env.REACT_APP_NEW_PRICING_LIVE === "true";

const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const Check = () => (
  <span className="upg-price-check" style={{ color: ACCENT }}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M2.5 7.5l3 3 6-7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const ArrowRight = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// Unpaid accounts get the hard paywall (same as the app's Upgrade button);
// subscribers keep this page for credits and plan changes.
const UpgradePage = () => {
  const [unpaid, setUnpaid] = useState(null);
  const navigateTo = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("tab") === "credits") { setUnpaid(false); return; }
    axiosInstance.get("/billing/plan/status")
      .then((r) => setUnpaid(!r.data?.plan || r.data.plan === "none"))
      .catch(() => setUnpaid(false));
  }, []);
  if (unpaid === null) return null;
  if (unpaid) return <HardPaywall onClose={() => (window.history.length > 1 ? navigateTo(-1) : navigateTo("/home"))} />;
  return <UpgradePlans />;
};

const UpgradePlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [annual, setAnnual] = useState(true);
  const [packMultiplier, setPackMultiplier] = useState(1);
  const [loading, setLoading] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") === "credits" ? "credits" : "plans");

  const starterPrice = STARTER_MONTHLY;
  const proBasePrice = annual ? PRO_ANNUAL : PRO_MONTHLY;
  const premiumPrice = annual ? PREMIUM_ANNUAL : PREMIUM_MONTHLY;
  const proPrice = proBasePrice * packMultiplier;
  const proCredits = 300 * packMultiplier;
  const proVideoMin = 300 * packMultiplier;

  const handleSubscribe = async (planKey) => {
    if (!NEW_PRICING_LIVE) {
      window.open(
        "mailto:clipbaitla@gmail.com?subject=" +
          encodeURIComponent(`Early access — ${planKey} plan`),
        "_blank"
      );
      return;
    }
    setLoading(planKey);
    try {
      const referral = getRewardfulReferral();
      const res = await axiosInstance.post("/billing/plan/checkout", {
        planName: planKey,
        interval: annual && planKey !== "Starter" ? "annual" : "monthly",
        ...(planKey === "Pro" ? { credits: proCredits } : {}),
        ...(referral ? { referral } : {}),
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(null);
    }
  };

  const handleBuyCredits = async (packId) => {
    setLoading(packId);
    try {
      const res = await axiosInstance.post("/billing/credits/checkout", { packId });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Credits checkout error:", err);
      setLoading(null);
    }
  };

  const ctaLabel = NEW_PRICING_LIVE ? "Get Plan" : "Request access";

  return (
    <div className="upg-page">
      <button className="upg-back" onClick={() => navigate(-1)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="upg-inner">
        <div className="upg-section-head">
          <h2 className="upg-section-h2">Explore plans</h2>

          <div className="upg-tabs">
            <button
              className={`upg-tab ${tab === "plans" ? "on" : ""}`}
              onClick={() => setTab("plans")}
            >
              Plans
            </button>
            <button
              className={`upg-tab ${tab === "credits" ? "on" : ""}`}
              onClick={() => setTab("credits")}
            >
              Buy Credits
            </button>
          </div>

          {tab === "plans" && (
            <div className="upg-bill-toggle">
              <button
                className={`upg-bill-btn ${!annual ? "on" : ""}`}
                onClick={() => setAnnual(false)}
              >
                Monthly
              </button>
              <button
                className={`upg-bill-btn ${annual ? "on" : ""}`}
                onClick={() => setAnnual(true)}
              >
                Annual <span className="upg-bill-save">20% off</span>
              </button>
            </div>
          )}
        </div>

        {tab === "plans" && (
          <>
            <div className="upg-price-grid">
              {/* STARTER */}
              <div className="upg-price-card">
                <div className="upg-price-name">Starter</div>
                <div className="upg-price-pitch">For solo clippers and streamers getting started.</div>
                <div className="upg-price-amount">
                  <span className="upg-price-currency">$</span>
                  <span className="upg-price-value">{fmt(starterPrice)}</span>
                  <span className="upg-price-period">
                    <span className="upg-price-period-sub">/mo</span>
                  </span>
                </div>
                <div className="upg-price-credit-pill">150 credits/mo · 150 min of video</div>
                {annual && <div className="upg-price-fineprint">Starter plan only available in monthly</div>}
                <button
                  className="upg-btn-secondary upg-price-cta"
                  onClick={() => handleSubscribe("Starter")}
                  disabled={loading === "Starter"}
                >
                  {loading === "Starter" ? (
                    <span className="upg-spinner" />
                  ) : (
                    <>{ctaLabel} <ArrowRight /></>
                  )}
                </button>
                <div className="upg-price-fineprint">Cancel anytime</div>
                <ul className="upg-price-list">
                  <li><Check />150 credits/mo (~150 min of video)</li>
                  <li><Check />9:16 + 16:9 aspect ratios</li>
                  <li><Check />HD rendered clips</li>
                  <li><Check />AI animated captions</li>
                  <li><Check />Watermark-free exports</li>
                  <li><Check />5 streamers</li>
                  <li><Check />Music recommendations for clips</li>
                </ul>
              </div>

              {/* PRO */}
              <div className="upg-price-card upg-price-hl">
                <div className="upg-price-tag" style={{ background: ACCENT }}>Most Popular</div>
                <div className="upg-price-name">Pro</div>
                <div className="upg-price-pitch">For full-time clippers and serious streamers.</div>
                <div className="upg-price-amount">
                  <span className="upg-price-currency">$</span>
                  <span className="upg-price-value">{fmt(proPrice)}</span>
                  <span className="upg-price-period">
                    <span className="upg-price-period-sub">/mo</span>
                  </span>
                </div>
                <div className="upg-price-credit-pill upg-price-credit-pill-on">
                  {proCredits.toLocaleString()} credits/mo · {proVideoMin.toLocaleString()} min of video
                </div>

                <CreditSlider value={packMultiplier} onChange={setPackMultiplier} />

                <button
                  className="upg-btn-primary upg-price-cta"
                  style={{ background: ACCENT }}
                  onClick={() => handleSubscribe("Pro")}
                  disabled={loading === "Pro"}
                >
                  {loading === "Pro" ? (
                    <span className="upg-spinner" />
                  ) : (
                    <>{ctaLabel} <ArrowRight /></>
                  )}
                </button>
                <div className="upg-price-fineprint">Cancel anytime</div>
                <ul className="upg-price-list">
                  <li><Check /><b>{proCredits.toLocaleString()} credits/mo</b> (~{proVideoMin} min of video)</li>
                  <li><Check />9:16 + 16:9 aspect ratios</li>
                  <li><Check />HD rendered clips</li>
                  <li><Check />AI animated captions</li>
                  <li><Check />Watermark-free exports</li>
                  <li><Check /><b>5 streamers</b></li>
                  <li><Check />Music recommendations for clips</li>
                  <li><Check />Hook-detection tuned for livestreams</li>
                  <li><Check />Auto-clip every stream you go live on</li>
                  <li><Check />Priority render queue</li>
                </ul>
              </div>

              {/* PREMIUM */}
              <div className="upg-price-card">
                <div className="upg-price-tag upg-price-tag-outline">Most Credits</div>
                <div className="upg-price-name">Premium</div>
                <div className="upg-price-pitch">For agencies, networks, and serious clipping operations.</div>
                <div className="upg-price-amount">
                  <span className="upg-price-currency">$</span>
                  <span className="upg-price-value">{fmt(premiumPrice)}</span>
                  <span className="upg-price-period">
                    <span className="upg-price-period-sub">/mo</span>
                  </span>
                </div>
                <div className="upg-price-credit-pill">1,500 credits/mo · 25 hours of video</div>
                <button
                  className="upg-btn-secondary upg-price-cta"
                  onClick={() => handleSubscribe("Premium")}
                  disabled={loading === "Premium"}
                >
                  {loading === "Premium" ? (
                    <span className="upg-spinner" />
                  ) : (
                    <>{ctaLabel} <ArrowRight /></>
                  )}
                </button>
                <div className="upg-price-fineprint">Cancel anytime</div>
                <ul className="upg-price-list">
                  <li><Check />1,500 credits/mo (~25 hours of video)</li>
                  <li><Check />9:16 + 16:9 aspect ratios</li>
                  <li><Check />HD rendered clips</li>
                  <li><Check />AI animated captions</li>
                  <li><Check />Watermark-free exports</li>
                  <li><Check /><b>5 streamers</b></li>
                  <li><Check />Music recommendations for clips</li>
                  <li><Check /><b>Most credits for heavy clippers</b></li>
                  <li><Check />Multi-creator workspace</li>
                  <li><Check />Dedicated support</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {tab === "credits" && (
          <div className="upg-credits-section">
            <p className="upg-credits-intro">
              Top up your account with one-time credits. Credits never expire and work on top of your plan.
            </p>
            <div className="upg-credits-grid">
              {CREDIT_PACKS.map((pack) => (
                <div key={pack.id} className="upg-credit-card">
                  <div className="upg-credit-amount">
                    <span className="upg-credit-num">{pack.credits}</span>
                    <span className="upg-credit-unit">credits</span>
                  </div>
                  <div className="upg-credit-desc">{pack.desc}</div>
                  <div className="upg-credit-price-row">
                    <span className="upg-credit-price">${pack.price}</span>
                    {pack.note && <span className="upg-credit-note">{pack.note}</span>}
                  </div>
                  <button
                    className="upg-btn-secondary upg-credit-cta"
                    onClick={() => handleBuyCredits(pack.id)}
                    disabled={loading === pack.id}
                  >
                    {loading === pack.id ? (
                      <span className="upg-spinner" />
                    ) : (
                      <>Buy <ArrowRight /></>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradePage;
